import { useMemo, memo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.3 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 80,
    },
  },
};

const imageHover = {
  scale: 1.05,
  transition: { duration: 0.3 },
};

const FeaturedBlog = ({ blogImages }) => {
  const { t } = useTranslation();
  const featuredBlog = t("components.home.featuredBlog", {
    returnObjects: true,
  });

  const blogPosts = useMemo(() => {
    const posts = featuredBlog.posts;

    const imageArray = [blogImages.post1, blogImages.post2, blogImages.post3];

    return posts.map((post, index) => ({
      ...post,
      image: imageArray[index] || imageArray[0],
    }));
  }, [featuredBlog.posts, blogImages]);

  return (
    <section className="w-full px-4 py-12 bg-black" id="safePad">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center mb-8 gap-4"
      >
        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
          {featuredBlog.heading}
        </h2>
        <Link to={featuredBlog.visitPageLink} aria-label="Read all blog posts">
          <motion.button
            whileHover={{ scale: 1.1, backgroundColor: "#009fd6" }}
            transition={{ type: "spring", stiffness: 120 }}
            className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-6 py-3 rounded-lg hover:bg-[#007cab] transition-colors cursor-pointer"
          >
            {featuredBlog.buttons.readAllBlog}
          </motion.button>
        </Link>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8"
      >
        <motion.div
          variants={cardVariants}
          className="relative bg-[#111111] rounded-xl shadow-md transition-shadow duration-300 overflow-hidden hover:shadow-lg group h-full"
        >
          <div className="absolute top-0 left-0 right-0 h-[3px] scale-x-0 z-10 bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450] transition-transform duration-300 group-hover:scale-x-100"></div>

          <motion.img
            src={blogPosts[0].image}
            alt={blogPosts[0].title}
            className="w-full h-[400px] object-cover z-0"
            whileHover={imageHover}
          />

          <div className="p-4 z-10">
            <span className="text-sm text-gray-400">{blogPosts[0].date}</span>
            <h3 className="text-md md:text-xl font-semibold mt-2 text-white transition-colors">
              {blogPosts[0].title}
            </h3>
            {blogPosts[0].category && (
              <span className="text-sm text-gray-500">
                {blogPosts[0].category}
              </span>
            )}
          </div>
        </motion.div>

        <div className="grid grid-rows-2 gap-8">
          {blogPosts.slice(1).map((post, index) => (
            <motion.div
              key={post.id || `blog-post-${index + 2}`}
              variants={cardVariants}
              className="relative flex gap-4 bg-[#111111] rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300 group h-full"
            >
              <div className="absolute top-0 left-0 right-0 h-[3px] scale-x-0 bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450] transition-transform duration-300 group-hover:scale-x-100"></div>
              <motion.img
                src={post.image}
                alt={post.title}
                className="w-1/3 md:w-1/2 h-auto object-cover rounded-lg"
                whileHover={imageHover}
              />
              <div className="flex flex-col justify-center">
                <span className="text-sm text-gray-400">{post.date}</span>
                <h3 className="text-md md:text-xl font-semibold text-white transition-colors">
                  {post.title}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

FeaturedBlog.propTypes = {
  blogImages: PropTypes.shape({
    post1: PropTypes.string.isRequired,
    post2: PropTypes.string.isRequired,
    post3: PropTypes.string.isRequired,
  }).isRequired,
};

export default memo(FeaturedBlog);
